/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject, signal } from '@angular/core';
import { OneSignal } from 'onesignal-ngx';
import { BehaviorSubject, Observable, from, timer } from 'rxjs';
import { DeviceDetectorService } from './device-detector.service';

export interface OneSignalConfig {
  autoRegister?: boolean;
  serviceWorkerParams?: {
    scope: string;
  };
  allowLocalhostAsSecureOrigin?: boolean;
}

export interface OneSignalAppIdCatalog {
  web: string;
  [key: string]: string;
}

@Injectable({
  providedIn: 'root',
})
export class OneSignalLoaderService {
  private _oneSignal = inject(OneSignal);
  private _deviceDetector = inject(DeviceDetectorService);
  private _initialized = signal(false);
  private _pushSubscriptionId = signal<string | undefined>(undefined);
  private _permissionStatus = new BehaviorSubject<boolean | undefined>(undefined);
  private _appIdCatalog: OneSignalAppIdCatalog = {
    web: '86073f44-47dc-45f6-9c18-aa1c9531140b', // ID para Windows, Android y otros
    mac: '86073f44-47dc-45f6-9c18-aa1c9531140b', // ID para Mac (actualmente igual al web, pero puede cambiarse)
  };

  /**
   * Configura el catálogo de appIds para OneSignal por plataforma
   * @param appIdCatalog El catálogo de appIds por plataforma
   */
  setAppIdCatalog(appIdCatalog: OneSignalAppIdCatalog): void {
    this._appIdCatalog = { ...this._appIdCatalog, ...appIdCatalog };
  }

  /**
   * Obtiene el appId apropiado según el dispositivo actual
   * @returns El appId correspondiente al dispositivo o undefined si no hay uno configurado
   */
  private _getAppIdForCurrentDevice(): string | undefined {
    return this._appIdCatalog?.web || '';
  }

  /**
   * Initializes OneSignal automáticamente detectando el tipo de dispositivo
   * y utilizando el appId correspondiente.
   * @param config Configuración adicional (opcional)
   * @returns Promise que se resuelve cuando OneSignal es inicializado, o null si no hay appId para el dispositivo
   */
  initialize(config?: Partial<OneSignalConfig>): Promise<void> | null {
    if (this._initialized()) {
      console.log('OneSignal already initialized');
      return Promise.resolve();
    }

    // Obtener el appId según el dispositivo actual
    const appId = this._getAppIdForCurrentDevice();

    console.log('OneSignal appId for current device type:', appId, this._appIdCatalog);

    // Si no hay appId configurado para este dispositivo, no inicializar
    if (!appId) {
      console.log('No OneSignal appId configured for current device type, initialization skipped');
      return null;
    }

    // Crear la configuración completa
    const fullConfig: OneSignalConfig & { appId: string } = {
      appId,
      autoRegister: config?.autoRegister ?? false,
      serviceWorkerParams: config?.serviceWorkerParams ?? { scope: '/' },
      allowLocalhostAsSecureOrigin: config?.allowLocalhostAsSecureOrigin ?? true,
    };

    console.log(
      `Initializing OneSignal for ${this._deviceDetector.getOS()} device with appId: ${appId}`
    );

    return this._oneSignal
      .init(fullConfig)
      .then(() => {
        console.log('OneSignal initialized successfully');
        this._initialized.set(true);

        // Get current subscription ID
        this._pushSubscriptionId.set(this._oneSignal.User.PushSubscription.id ?? undefined);

        // Update permission status
        this._updatePermissionStatus();

        // Set up subscription listener for changes
        this._oneSignal.User.PushSubscription.addEventListener('change', () => {
          this._pushSubscriptionId.set(this._oneSignal.User.PushSubscription.id || undefined);
        });
      })
      .catch((error) => {
        console.error('OneSignal initialization error:', error);
        throw error;
      });
  }

  getNotificationPermission(): string {
    return this._oneSignal.Notifications.permissionNative;
  }

  /**
   * Requests push notification permission from the user
   * @returns Promise that resolves with the permission status
   */
  requestPushPermission(): Promise<boolean> {
    if (!this._initialized()) {
      return Promise.reject('OneSignal not initialized');
    }

    return new Promise<boolean>((resolve) => {
      void this._oneSignal.Notifications.requestPermission()
        .then((_permission) => {
          timer(100).subscribe(() => {
            this._updatePermissionStatus();
            resolve(this._oneSignal?.Notifications.permission);
          });
        })
        .catch((_error) => {
          resolve(false);
        });
    });
  }

  /**
   * Gets the current OneSignal push subscription ID
   */
  get pushSubscriptionId(): string {
    return this._pushSubscriptionId() || '';
  }

  /**
   * Observable that emits the current push subscription ID and when it changes
   */
  get pushSubscriptionId$(): Observable<string | undefined> {
    return from(Promise.resolve(this._oneSignal.User.PushSubscription.id || undefined));
  }

  /**
   * Gets the current permission status
   */
  get permissionStatus$(): Observable<boolean | undefined> {
    return this._permissionStatus.asObservable();
  }

  /**
   * Checks if the user has granted notification permission
   */
  get hasPermission(): boolean {
    return this._permissionStatus.value === true;
  }

  /**
   * Updates the internal permission status
   */
  private _updatePermissionStatus(): void {
    Promise.resolve(this._oneSignal.Notifications.permission).then((permission: any) => {
      this._permissionStatus.next(permission);
    });
  }

  /**
   * Adds a tag to the user's profile
   * @param key Tag key
   * @param value Tag value
   */
  addTag(key: string, value: string): Promise<void> {
    return Promise.resolve(this._oneSignal.User.addTag(key, value));
  }

  /**
   * Adds multiple tags to the user's profile
   * @param tags Object containing key-value pairs for tags
   */
  addTags(tags: Record<string, string>): Promise<void> {
    return Promise.resolve(this._oneSignal.User.addTags(tags));
  }

  /**
   * Removes a tag from the user's profile
   * @param key Tag key to remove
   */
  removeTag(key: string): Promise<void> {
    return Promise.resolve(this._oneSignal.User.removeTag(key));
  }

  /**
   * Gets all tags for the current user
   */
  getTags(): Promise<Record<string, string>> {
    return Promise.resolve(this._oneSignal.User.getTags() as Record<string, string>);
  }

  /**
   * Desactiva temporalmente las notificaciones push
   * No revoca los permisos, solo pausa la recepción de notificaciones
   * @returns Promise que se resuelve cuando las notificaciones son desactivadas
   */
  disableNotifications(): Promise<void> {
    if (!this._initialized()) {
      return Promise.reject('OneSignal not initialized');
    }

    console.log('Disabling OneSignal notifications');
    // Usar el método optOut de OneSignal para deshabilitar
    return Promise.resolve(this._oneSignal.User.PushSubscription.optOut());
  }

  /**
   * Reactiva las notificaciones push si fueron desactivadas
   * No solicita permisos nuevamente si ya fueron otorgados
   * @returns Promise que se resuelve cuando las notificaciones son reactivadas
   */
  enableNotifications(): Promise<void> {
    if (!this._initialized()) {
      return Promise.reject('OneSignal not initialized');
    }

    console.log('Enabling OneSignal notifications');
    // Usar el método optIn de OneSignal para habilitar
    return Promise.resolve(this._oneSignal.User.PushSubscription.optIn());
  }

  /**
   * Verifica si las notificaciones están actualmente activas (suscrito)
   * @returns boolean indicando si el usuario está suscrito a notificaciones
   */
  get isSubscribed(): boolean {
    if (!this._initialized()) {
      return false;
    }
    return this._oneSignal.User.PushSubscription.optedIn || false;
  }

  /**
   * Observable que emite el estado actual de suscripción y cuando cambia
   * @returns Observable con el estado de suscripción (boolean)
   */
  get isSubscribed$(): Observable<boolean> {
    if (!this._initialized()) {
      return from(Promise.resolve(false));
    }
    return from(Promise.resolve(this._oneSignal.User.PushSubscription.optedIn || false));
  }
}
